<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { Leaf, Moon, Sun } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import AppSidebar from '@/components/AppSidebar.vue'
import GameIcon from '@/components/GameIcon.vue'
import OcrStartupGate from '@/components/OcrStartupGate.vue'
import SessionStartIssueDialog from '@/components/SessionStartIssueDialog.vue'
import UpdateAnnouncementDialog from '@/components/UpdateAnnouncementDialog.vue'
import { useAppStore } from '@/stores/app'
import { useLiveStore } from '@/stores/live'
import { GAME_ICONS } from '@/ui/game-icons'

const { t } = useI18n()
const app = useAppStore()
const live = useLiveStore()
const route = useRoute()
const initialized = ref(false)
const startupDialogOccupied = ref(false)
const startIssueDialogOccupied = ref(false)
const announcementBlocked = computed(() => startupDialogOccupied.value || startIssueDialogOccupied.value
  || live.isSelectingWindow || live.isStarting || live.isEnding
  || Boolean(live.session && live.session.status !== 'ended'))
let publicAppInitialized = false

function initializeAfterFirstPaint(): void {
  if (publicAppInitialized) return
  publicAppInitialized = true
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      void live.initialize().finally(() => { initialized.value = true })
    })
  })
}

function preparePageEnter(element: Element): void {
  element.removeAttribute('inert')
}

function preparePageLeave(element: Element): void {
  element.setAttribute('inert', '')
}

onMounted(initializeAfterFirstPaint)
watch(() => route.fullPath, async () => {
  await nextTick()
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  document.querySelector<HTMLElement>('#main-content')?.focus({ preventScroll: true })
})
</script>

<template>
  <a class="skip-link" href="#main-content">{{ t('app.skipToContent') }}</a>
  <OcrStartupGate @visibility-change="startupDialogOccupied = $event" />
  <SessionStartIssueDialog @visibility-change="startIssueDialogOccupied = $event" />
  <UpdateAnnouncementDialog :ready="initialized" :blocked="announcementBlocked" />
  <div class="app-shell">
    <AppSidebar />
    <header class="mobile-topbar">
      <RouterLink class="mobile-brand" to="/" :aria-label="t('app.name')">
        <span class="brand-mark"><GameIcon :src="GAME_ICONS.mapleLeaf" dimension="1.75rem" eager><Leaf stroke-width="2.2" /></GameIcon></span>
        <span>{{ t('app.name') }}</span>
      </RouterLink>
      <button class="icon-btn topbar-theme" type="button" :title="app.theme === 'light' ? t('app.useDark') : t('app.useLight')" :aria-label="app.theme === 'light' ? t('app.useDark') : t('app.useLight')" @click="app.toggleTheme">
        <Moon v-if="app.theme === 'light'" style="width:1.125rem;height:1.125rem" aria-hidden="true" />
        <Sun v-else style="width:1.125rem;height:1.125rem" aria-hidden="true" />
      </button>
    </header>
    <main id="main-content" class="app-main" tabindex="-1">
      <RouterView v-slot="{ Component, route: pageRoute }">
        <div class="route-stage">
          <Transition name="page" appear @before-enter="preparePageEnter" @before-leave="preparePageLeave" @leave-cancelled="preparePageEnter">
            <div v-if="Component" :key="pageRoute.path" class="route-page" :class="{ 'route-page--rank': pageRoute.name === 'rank' }">
              <component :is="Component" />
            </div>
          </Transition>
        </div>
      </RouterView>
    </main>
  </div>
</template>
